'use client';
import { hasCldImage, TypeBlockPageIntro } from '@fm-nx/contentful-types';
import { Icon } from '@fm-nx/svg';
import { CldImage } from 'next-cloudinary';
import { useState } from 'react';
import AnimationImage from '../animation-image/animation-image';
import useImageWidthResponsive from '../hooks/useImageWidthResponsive';

type PageIntroImageOverlayProps = {
    intro: TypeBlockPageIntro<'WITHOUT_UNRESOLVABLE_LINKS'>;
};

// technically this is a landing page intro I think
// Right now there is only one option: blockImageOverlayIntro
export const PageIntroImageOverlay = ({ intro }: PageIntroImageOverlayProps) => {
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const { containerRef, width } = useImageWidthResponsive();
    const { fields } = intro;

    const imageCloudinary = hasCldImage(fields.image?.fields) ? fields.image?.fields.cloudinaryMedia[0] : null;

    const handleExploreClick = () => {
        const element = document.querySelector('[data-content-type]') as HTMLElement;
        if (!window || !element) return;
        window.scrollTo({ behavior: 'smooth', top: element.offsetTop - 20 });
    };

    if (!fields) return null;

    return (
        <div className="relative">
            <div className="relative w-full h-[424px] md:h-screen md:max-h-268" ref={containerRef}>
                {/* TODO: Why does webp make the image load slower */}
                {imageCloudinary && (
                    <div className="absolute inset-0">
                        <AnimationImage isImageLoaded={isImageLoaded}>
                            <div className="z-10 absolute bottom-0 w-full bg-linear-to-b from-gray-900/50 to-transparent h-[424px] md:h-screen md:max-h-268"></div>
                            <CldImage
                                className="z-0 object-cover"
                                fill
                                src={`${imageCloudinary.public_id}`}
                                alt={fields.image?.fields?.title || ''}
                                sizes={width}
                                priority
                                onLoad={() => setIsImageLoaded(true)}
                                format="auto" // Automatically use WebP or AVIF based on browser support
                                quality="auto" // Automatically adjust quality based on network and device
                                dpr="auto" // Automatically adjust for device pixel ratio
                                fetchPriority="high" // This is a critical above-the-fold image
                                loading="eager" // Load immediately as it's above the fold
                            />
                        </AnimationImage>
                    </div>
                )}
                <div className="w-full mx-auto max-w-container">
                    <div className="mx-6 md:mx-0">
                        <div className="relative z-10 grid grid-cols-1 gap-0 md:grid-cols-14 md:grid-rows-3 h-[424px] md:h-screen md:max-h-268">
                            <div className="col-span-1 row-start-2 space-y-6 md:col-span-8 md:col-start-5 xl:col-start-6">
                                {fields.overline && (
                                    <span className="z-10 text-white h5 overline">{fields.overline}</span>
                                )}
                                <h2 className="text-white h-xl">{fields?.headerText}</h2>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="relative z-10 -mt-[116px] md:-mt-[182px] lg:-mt-[214px] xl:-mt-[278px]">
                <div className="w-full mx-auto max-w-container">
                    <div className="mx-6 md:mx-0 md:grid md:grid-cols-14">
                        <div className="md:col-span-12 md:col-start-2">
                            <div className="inline-block relative py-8 pr-12 bg-white before:content-[''] before:bg-white before:absolute before:h-full before:w-screen before:top-0 before:right-full md:py-16 md:pr-24 md:justify-center lg:py-20 lg:pr-40 xl:py-28 xl:pr-56">
                                <button
                                    onClick={() => handleExploreClick()}
                                    className="inline-flex items-center justify-start group"
                                >
                                    <span className="flex items-center justify-center group-hover:animate-bounce-down-short">
                                        <Icon name="arrow" direction="down" size="xl" />
                                    </span>
                                    <span className="pb-2 mt-2 ml-4 h-sm border-b-out">Explore</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PageIntroImageOverlay;
